// Phase P-LINK-VISA-TRANSLATE-REVERSE
// Reverse forward edges from /visa/$nationality/$visatype (and EN twin) into
// the triple-key matrix /translate/$language/$doctype/in/$location.
//
// Why: visa applicants almost always need 2-3 specific translated documents
// (e.g. Marriage Visa → ทะเบียนสมรส + financial statement; Non-B → company
// affidavit + contract; Retirement O-A → medical certificate + police
// clearance). Deep-linking into the triple-key page closes the journey and
// funnels PageRank from visa pillars → long-tail triple-key pages.

import { Link } from "@tanstack/react-router";
import { ArrowRight, Languages } from "lucide-react";
import { getTranslationDocType } from "@/data/translation-doctypes";

// Visa type id → ordered list of most-relevant translation doctype slugs.
// Kept short (max 4) to focus PageRank on the most-converting combos.
const VISA_TO_DOCTYPES: Record<string, string[]> = {
  "non-b": ["company-affidavit", "contract", "financial-statement", "transcript"],
  "smart-visa": ["financial-statement", "contract", "diploma", "transcript"],
  "ltr-visa": ["financial-statement", "passport", "medical-certificate"],
  "elite-visa": ["passport", "financial-statement"],
  "dtv-visa": ["financial-statement", "passport", "contract"],
  "retirement-o-a": ["medical-certificate", "financial-statement", "police-clearance"],
  "retirement-o-x": ["medical-certificate", "financial-statement", "police-clearance"],
  "marriage-visa": ["marriage-certificate", "birth-certificate", "financial-statement"],
  "ed-visa": ["diploma", "transcript", "passport"],
  "tourist-tr": ["passport", "financial-statement"],
};

// Hub location whitelist — must stay subset of TRIPLE_KEY_LOCATIONS in
// src/routes/translate.$language.$doctype.in.$location.tsx
const DEFAULT_HUBS = [
  "bangkok-hq",
  "hub-asoke-corridor",
  "hub-sathorn-cbd",
  "hub-phuket-patong",
] as const;

interface Props {
  mode: "th" | "en";
  visaTypeId: string;       // e.g. "marriage-visa"
  visaCode: string;         // e.g. "Non-O Marriage"
  language: string | undefined; // triple-key language slug, e.g. "english"
  languageNameTh: string;   // e.g. "ภาษาอังกฤษ"
  languageNameEn: string;   // e.g. "English"
  nationalityNameTh: string;// e.g. "ชาวอเมริกัน"
  nationalityDemonymEn: string; // e.g. "American"
}

const HUB_LABEL_TH: Record<string, string> = {
  "bangkok-hq": "สำนักงานใหญ่ กรุงเทพฯ",
  "hub-asoke-corridor": "อโศก / สุขุมวิท",
  "hub-sathorn-cbd": "สาทร CBD",
  "hub-phuket-patong": "ภูเก็ต ป่าตอง",
};
const HUB_LABEL_EN: Record<string, string> = {
  "bangkok-hq": "Bangkok HQ",
  "hub-asoke-corridor": "Asoke / Sukhumvit",
  "hub-sathorn-cbd": "Sathorn CBD",
  "hub-phuket-patong": "Phuket / Patong",
};

export default function VisaTranslateDeepLinks({
  mode, visaTypeId, visaCode, language,
  languageNameTh, languageNameEn,
  nationalityNameTh, nationalityDemonymEn,
}: Props) {
  if (!language) return null;
  const doctypes = (VISA_TO_DOCTYPES[visaTypeId] ?? ["passport", "financial-statement"])
    .map((slug) => ({ slug, d: getTranslationDocType(slug) }))
    .filter((x) => x.d) as { slug: string; d: NonNullable<ReturnType<typeof getTranslationDocType>> }[];
  if (doctypes.length === 0) return null;

  const isTh = mode === "th";
  // Build (doctype × hub) pairs — round-robin assign hubs so we don't repeat
  // the same hub on every card. Cap at 6 cards.
  const cards = doctypes.flatMap((dt, i) => {
    const hub = DEFAULT_HUBS[i % DEFAULT_HUBS.length];
    return [{ ...dt, hub }];
  }).slice(0, 6);

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
        {isTh ? "แปลรับรองสำหรับ" : "Certified translation for"} {visaCode}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh
          ? `แปลเอกสาร${languageNameTh}สำหรับ${nationalityNameTh}ที่ยื่น ${visaCode}`
          : `Translate documents into ${languageNameEn} for ${nationalityDemonymEn} ${visaCode} applicants`}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        {isTh
          ? `เอกสารหลักที่ ${nationalityNameTh} ส่วนใหญ่ต้องแปล${languageNameTh}คู่กับ ${visaCode} — รับรอง MoJ + ตรา MFA ครบในที่เดียว นัดรับที่ฮับใกล้คุณ`
          : `The core paperwork ${nationalityDemonymEn} ${visaCode} applicants need translated into ${languageNameEn} — MoJ-sworn + MFA-stamped, pick-up at the hub nearest you.`}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ slug, d, hub }) => (
          <Link
            key={`${slug}-${hub}`}
            to={isTh
              ? "/translate/$language/$doctype/in/$location"
              : "/en/translate/$language/$doctype/in/$location"}
            params={{ language, doctype: slug, location: hub }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            data-track-zone="visa-translate-deep"
          >
            <Languages className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
                {isTh ? d.nameTh : d.nameEn}
              </div>
              <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                {isTh
                  ? `แปล ${d.nameTh} เป็น${languageNameTh}`
                  : `${d.nameEn} → ${languageNameEn}`}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {isTh ? `รับ/นัดที่ ${HUB_LABEL_TH[hub] ?? hub}` : `Pick-up at ${HUB_LABEL_EN[hub] ?? hub}`}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 group-hover:gap-1.5">
                {isTh ? "ดูราคา + ขั้นตอน" : "See price + steps"} <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
